/**
 * @file 素材库数据类型定义
 */

import type {CanvasTypeValue} from '@/types/canvas';
import type {MediaTypeCode} from '@/types/media';
import type {ProjectId} from '@/types';

/** 素材类型 */
export const MaterialTypeValue = {
    /** 角色 */
    CHARACTER: 1,
    /** 场景 */
    SCENE: 2,
    /** 道具 */
    PROP: 3,
    /** 艺术定调 */
    ART_STYLE: 4,
    /** 分镜 */
    STORYBOARD: 5,
} as const;
export type MaterialType = (typeof MaterialTypeValue)[keyof typeof MaterialTypeValue];

/** 素材库 Tab 配置请求参数 */
export interface MaterialTabConfigParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 来源类型，对应 canvasType */
    sourceType?: CanvasTypeValue;
}

/** 素材列表请求参数 */
export interface MaterialListParams {
    /** 动态筛选条件，key 来自 /material/tab/config 的 filterTabs */
    [key: string]: ProjectId | number | boolean | undefined;
    /** 项目ID */
    projectId: ProjectId;
    /** 集ID */
    episodeId?: number;
    /** 来源类型，对应 canvasType */
    sourceType?: CanvasTypeValue;
    /** 当前画布集，仅分镜画布传入 */
    currentCanvasEpisode?: number;
    /** 类型：1-角色 2-场景 3-道具 4-艺术定调 5-分镜，不传时默认1-角色 */
    type: MaterialType;
    /** 文件类型：1-图片 2-视频 3-音频 */
    fileType?: MediaTypeCode;
    /** 页码 */
    page: number;
    /** 每页条数 */
    pageSize: number;
}

/** 素材项 */
export interface MaterialItem {
    /** 数据ID */
    id: number;
    /** 生成ID */
    genId: string | number;
    /** 文件类型：1-图片 2-视频 3-音频 */
    fileType: MediaTypeCode;
    /** 封面图 */
    coverUrl?: string;
    /** 图片/视频/音频URL */
    fileUrl: string;
    /** 宽度（像素） */
    width: number;
    /** 高度（像素） */
    height: number;
    /** 时长（秒），音频/视频有值 */
    duration?: number;
    /** 是否本集 */
    currentEpisode?: boolean;
    /** 是否首选 */
    isPrimary: boolean;
    /** 图片文案 */
    copywriting: string;
}

/** Tab 筛选项 */
export interface MaterialFilterTab {
    /** 查询参数 key */
    key: string;
    /** 筛选项展示文案 */
    label: string;
}

/** Tab 二级标签 */
export interface MaterialSecondTab {
    /** 1-图片 2-视频 3-音频 */
    value: MediaTypeCode;
    /** 图片/视频/音频 */
    label: string;
    /** 二级 tab 下的筛选项 */
    filterTabs?: MaterialFilterTab[];
}

/** Tab 集选项 */
export interface MaterialSelection {
    /** 集ID */
    value: number;
    /** 集数 */
    label: string;
}

/** Tab 配置项 */
export interface MaterialTabConfig {
    /** 1-角色 2-场景 3-道具 4-艺术定调 5-分镜素材 */
    value: MaterialType;
    /** 角色/场景/道具/艺术定调/分镜素材 */
    label: string;
    /** 比例 */
    ratio?: string;
    /** 一级 tab 下的筛选项 */
    filterTabs?: MaterialFilterTab[];
    /** 二级tab */
    secondTabs?: MaterialSecondTab[];
    /** 集选项 */
    selections?: MaterialSelection[];
}

/** Tab 配置 */
export interface MaterialTabConfigData {
    /** Tab 列表 */
    tabs: MaterialTabConfig[];
}

/** 素材库 Tab 配置响应 data */
export interface MaterialTabConfigResponse {
    /** Tab 列表 */
    tabConfig: MaterialTabConfig[];
}

/** 素材列表响应 data */
export interface MaterialListData {
    /** 当前页码 */
    page: number;
    /** 每页条数 */
    pageSize: number;
    /** 总数 */
    total: number;
    /** 素材列表 */
    list: MaterialItem[];
    /** Tab 配置 */
    tabConfig: MaterialTabConfigData;
}

/** ****** 画风列表接口请求/响应类型定义 ****** */

/** 画风列表请求参数 */
export interface ArtStyleListParams {
    /** 项目ID */
    projectId?: ProjectId;
    /** 风格类型：0-官方 1-自定义 */
    styleType?: number;
}

/** 画风项 */
export interface ArtStyleItem {
    /** 画风ID */
    styleId: number;
    /** 画风名称 */
    styleName: string;
    /** 封面图URL */
    coverUrl: string;
    /** 风格描述 */
    description?: string;
    /** 风格提示词 */
    prompt?: string;
    /** 状态：0-禁用 1-可用 */
    status: number;
}

/** 画风分组 */
export interface ArtStyleGroup {
    /** 类型：0-官方 1-自定义 */
    type: number;
    /** 画风列表 */
    styleList: ArtStyleItem[];
}

/** 画风列表响应 data */
export interface ArtStyleListData {
    /** 画风分组列表 */
    artStyleInfo: ArtStyleGroup[];
}

/** ****** 新建自定义画风接口请求/响应类型定义 ****** */

/** 新建自定义画风请求参数 */
export interface CreateArtStyleParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 画风名称 */
    styleName: string;
    /** 样例图URL */
    coverUrl: string;
    /** 生成提示词，不填则自动生成 */
    prompt?: string;
    /** 风格描述 */
    description?: string;
}

/** 更新自定义画风请求参数 */
export interface UpdateArtStyleParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 画风ID */
    styleId: number;
    /** 画风名称 */
    styleName: string;
    /** 样例图URL */
    coverUrl: string;
    /** 生成提示词，不填则自动生成 */
    prompt?: string;
    /** 风格描述 */
    description?: string;
}

/** 删除自定义画风请求参数 */
export interface DeleteArtStyleParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 画风ID */
    styleId: number;
}

/** 图片 Prompt 生成请求参数 */
export interface ImagePromptGenerateParams {
    /** 项目 ID */
    projectId: ProjectId;
    /** 图片 URL */
    imageUrl: string;
}

/** 图片 Prompt 生成响应 data */
export interface ImagePromptGenerateData {
    /** 生成的风格提示词 */
    prompt: string;
}

/** 素材模板类型 */
export const TemplateTypeValue = {
    /** 角色 */
    CHARACTER: 1,
    /** 场景 */
    SCENE: 2,
    /** 道具 */
    PROP: 3,
    /** 动作 */
    ACTION: 6,
} as const;
export type TemplateType = (typeof TemplateTypeValue)[keyof typeof TemplateTypeValue];

/** 素材模板列表请求参数 */
export interface TemplateListParams {
    /** 类型：1-角色 2-场景 3-道具 6-动作 */
    type: TemplateType;
    /** 页码 */
    page: number;
    /** 每页条数 */
    pageSize: number;
}

/** 素材模板项（前端消费形态） */
export interface TemplateItem {
    /** 模板ID */
    id: string | number;
    /** 文件类型：1-图片 2-视频 3-音频 */
    fileType: MediaTypeCode;
    /** 图片/视频/音频URL */
    fileUrl: string;
    /** 封面图 */
    coverUrl?: string;
    /** 名称 */
    name: string;
    /** 描述 */
    description: string;
    /** 宽度（像素） */
    width: number;
    /** 高度（像素） */
    height: number;
    /** 生成ID */
    genId?: string | number;
    /** 时长（秒），仅音频/视频 */
    duration?: number;
    /** 是否会员专属 */
    isMemberOnly?: boolean;
}

/** 素材模板项（后端响应形态） */
export interface TemplateListResponseItem extends Omit<TemplateItem, 'isMemberOnly'> {
    /** 是否会员专属 */
    memberOnly?: boolean;
}

/** 素材模板 Tab 配置项 */
export interface TemplateTabConfig {
    /** 1-角色 2-场景 3-道具 6-动作 */
    value: TemplateType;
    /** 角色库/场景库/道具库/动作库 */
    label: string;
    /** 比例 */
    ratio?: string;
}

/** 素材模板 Tab 配置 */
export interface TemplateTabConfigData {
    /** Tab 列表 */
    tabs: TemplateTabConfig[];
}

/** 素材模板列表响应 data（后端原始形态） */
export interface TemplateListResponseData {
    /** 当前页码 */
    page: number;
    /** 每页条数 */
    pageSize: number;
    /** 总数 */
    total: number;
    /** 素材列表 */
    list: TemplateListResponseItem[];
    /** Tab 配置 */
    tabConfig: TemplateTabConfigData;
}

/** 素材模板列表响应 data（前端消费形态） */
export interface TemplateListData {
    /** 当前页码 */
    page: number;
    /** 每页条数 */
    pageSize: number;
    /** 总数 */
    total: number;
    /** 素材列表 */
    list: TemplateItem[];
    /** Tab 配置 */
    tabConfig: TemplateTabConfigData;
}

/** 提交素材类型：1-角色装扮 2-场景 3-道具 4-艺术定调 5-分镜 */
export const SubmitMaterialTypeValue = {
    /** 角色装扮 */
    CHARACTER_OUTFIT: 1,
    /** 场景 */
    SCENE: 2,
    /** 道具 */
    PROP: 3,
    /** 艺术定调 */
    ART_STYLE: 4,
    /** 分镜 */
    STORYBOARD: 5,
} as const;
export type SubmitMaterialType =
    (typeof SubmitMaterialTypeValue)[keyof typeof SubmitMaterialTypeValue];

/** 分镜素材子类型：type=5 时使用 */
export const StoryboardMaterialTypeValue = {
    /** 参考图 */
    REF_IMAGE: 1,
    /** 参考视频 */
    REF_VIDEO: 2,
    /** 分镜图片 */
    IMAGE: 3,
    /** 分镜视频 */
    VIDEO: 4,
    /** 音频 */
    AUDIO: 5,
} as const;
export type StoryboardMaterialType =
    (typeof StoryboardMaterialTypeValue)[keyof typeof StoryboardMaterialTypeValue];

/** 提交素材请求参数（/proxy-aicomic/asset/join/material） */
export interface SubmitMaterialParams {
    /** 必填：项目ID */
    projectId: ProjectId;
    /** 必填：素材类型 1-角色装扮 2-场景 3-道具 4-艺术定调 5-分镜 */
    type: SubmitMaterialType;
    /** 必填：文件类型 1-图片 2-视频 3-音频 */
    fileType: MediaTypeCode;
    /** 必填：文件URL */
    url: string;
    /** 素材名称 */
    name?: string;
    /** 角色装扮id / 场景id / 道具id / 分镜id */
    id?: number;
    /** 角色装扮组id / 场景组id / 道具组id */
    setId?: number;
    /** 角色装扮组编码 / 场景组编码 / 道具组编码 */
    setCode?: string;
    /** 角色装扮组名称 / 场景组名称 / 道具组名称 */
    setName?: string;
    /** 集id（分镜时使用） */
    episodeId?: number;
    /** 分镜素材类型（type=5 时使用）1-参考图 2-参考视频 3-分镜图片 4-分镜视频 5-音频 */
    storyboardMaterialType?: StoryboardMaterialType;
    /** 素材生成ID，后端用于去重 */
    genId?: string;
    /** 时长（秒） */
    duration?: number;
    /** 高（像素） */
    height?: number;
    /** 宽（像素） */
    width?: number;
    /** 文件大小（字节） */
    size?: number;
    /** 分镜的版本号 */
    version?: number;
}
